import { useCallback, useMemo, useState } from "react"

import type { ModelsTableKind, ModelsTableRow } from "../components/models-table"

import {
  useAdminEmbeddingModelsQuery,
  useAdminModelsQuery,
  useAdminSpecializedModelsQuery,
} from "./admin-queries"

const TAB_STORAGE_KEY = "admin-models-tab"

const TABS: ModelsTableKind[] = ["chat", "embedding", "specialized"]

/** Cache key of the list each tab renders, so a status toggle patches the right one. */
const TAB_QUERY_KEYS: Record<ModelsTableKind, readonly unknown[]> = {
  chat: ["admin", "models"],
  embedding: ["admin", "embeddings"],
  specialized: ["admin", "specialized"],
}

function isTab(value: string): value is ModelsTableKind {
  return (TABS as string[]).includes(value)
}

function readStoredTab(): ModelsTableKind {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY)
    if (stored && isTab(stored)) return stored
  } catch {
    // Private mode / blocked storage: fall through to the default tab.
  }
  return "chat"
}

/**
 * The Models card shows chat, embedding and specialized registry entries in one
 * table behind a segmented filter. Each list has its own endpoint and its own
 * cache key, so every header action — enable-all, the per-row status switch —
 * has to act on the tab currently on screen rather than on the chat models.
 *
 * The last tab is remembered in `localStorage`, which admins expect when they
 * come back to a card they were already working in.
 */
export function useModelsTab() {
  const [activeTab, setActiveTab] = useState<ModelsTableKind>(readStoredTab)

  const { data: chatModels, isLoading: isLoadingChat } = useAdminModelsQuery()
  const { data: embeddingModels, isLoading: isLoadingEmbeddings } = useAdminEmbeddingModelsQuery()
  const { data: specializedModels, isLoading: isLoadingSpecialized } =
    useAdminSpecializedModelsQuery()

  const setTab = useCallback((value: string) => {
    if (!isTab(value)) return
    setActiveTab(value)
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, value)
    } catch {
      // Storage unavailable: the tab still switches, it just is not remembered.
    }
  }, [])

  const rows: ModelsTableRow[] = useMemo(() => {
    if (activeTab === "embedding") return embeddingModels ?? []
    if (activeTab === "specialized") return specializedModels ?? []
    return chatModels ?? []
  }, [activeTab, chatModels, embeddingModels, specializedModels])

  // The access dialogs can be opened from any tab, so their titles resolve
  // against every list rather than the chat models alone.
  const allRows: ModelsTableRow[] = useMemo(
    () => [...(chatModels ?? []), ...(embeddingModels ?? []), ...(specializedModels ?? [])],
    [chatModels, embeddingModels, specializedModels]
  )

  const isLoading = useMemo(() => {
    if (activeTab === "embedding") return isLoadingEmbeddings
    if (activeTab === "specialized") return isLoadingSpecialized
    return isLoadingChat
  }, [activeTab, isLoadingChat, isLoadingEmbeddings, isLoadingSpecialized])

  return {
    activeTab,
    setTab,
    rows,
    allRows,
    isLoading,
    queryKey: TAB_QUERY_KEYS[activeTab],
  }
}
